import * as React from 'react';

import { ConsoleDrawer, Skeleton } from '@rematter/ui-web';
import { cn } from '@rematter/ui-web/lib';
import { atom, useAtom, useAtomValue } from 'jotai';
import {
  LucideArrowRight,
  LucideCircle,
  LucideCircleDollarSign,
  LucideFilePlus2,
  LucidePencil,
  LucidePlus,
  LucideTrash2,
  LucideWeight,
} from 'lucide-react';

import {
  customPriceListHistoryQueryAtom,
  type PriceListHistoryEntry,
} from '../../_graphql/queryCustomPriceListHistory';
import { defaultPriceListHistoryQueryAtom } from '../../_graphql/queryDefaultPriceListHistory';
import { useTranslations } from '../../i18n/useTranslations';

interface PriceListHistoryDialogProps {
  isOpen: boolean;
  onClose: () => void;
  priceListId: string;
  priceListName: string;
  priceListType?: 'custom' | 'default';
  itemNameMap?: Map<string, string>;
}

const $historyPriceListId = atom<string | null>(null);

const $customHistoryData = customPriceListHistoryQueryAtom((get) => ({
  variables: { id: get($historyPriceListId) ?? '' },
  skip: !get($historyPriceListId),
}));

const $defaultHistoryData = defaultPriceListHistoryQueryAtom((get) => ({
  variables: { id: get($historyPriceListId) ?? '' },
  skip: !get($historyPriceListId),
}));

const EVENT_CONFIG: Record<
  string,
  { label: string; icon: React.ReactNode; color: string }
> = {
  created: {
    label: 'Price List Created',
    icon: <LucideFilePlus2 size={14} />,
    color: 'text-success',
  },
  itemPricingAdded: {
    label: 'Pricing Added',
    icon: <LucidePlus size={14} />,
    color: 'text-success',
  },
  itemPricingUpdated: {
    label: 'Pricing Updated',
    icon: <LucidePencil size={14} />,
    color: 'text-primary',
  },
  itemPricingDeleted: {
    label: 'Pricing Deleted',
    icon: <LucideTrash2 size={14} />,
    color: 'text-failure',
  },
  itemPricingBatchUpdated: {
    label: 'Batch Pricing Update',
    icon: <LucideCircleDollarSign size={14} />,
    color: 'text-primary',
  },
  published: {
    label: 'Price List Published',
    icon: <LucideCircle size={14} />,
    color: 'text-success',
  },
  archived: {
    label: 'Price List Archived',
    icon: <LucideCircle size={14} />,
    color: 'text-warning',
  },
  copiedFrom: {
    label: 'Copied From Another List',
    icon: <LucideCircleDollarSign size={14} />,
    color: 'text-primary',
  },
  importedFrom: {
    label: 'Imported From Another List',
    icon: <LucidePlus size={14} />,
    color: 'text-primary',
  },
  notificationSent: {
    label: 'Email Notification Sent',
    icon: <LucideCircle size={14} />,
    color: 'text-neutral/50',
  },
};

const formatEventType = (eventType: string): string =>
  EVENT_CONFIG[eventType]?.label ??
  eventType
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (s) => s.toUpperCase())
    .trim();

const formatDate = (dateStr: string): string => {
  try {
    const cleaned = dateStr.replace(/\[.*\]$/, '');
    const d = new Date(cleaned);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleString('en-US', {
      month: 'short',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return dateStr;
  }
};

const formatValue = (val: unknown): string => {
  if (val === null || val === undefined) return '';
  if (typeof val === 'boolean') return val ? 'Yes' : 'No';
  if (typeof val === 'object' && val !== null && 'value' in val)
    return String((val as { value: unknown }).value);
  return String(val);
};

type ProtoTier = {
  maxAmount?: string | null;
  targetPriceFormula?: string;
  minPriceFormula?: string;
  maxPriceFormula?: string;
  scrapValue?: string;
  position?: number;
};

type ProtoItemPricing = {
  id?: { value?: string };
  itemId?: { value?: string };
  pricingUnit?: string;
  tiersEffectiveAt?: string;
  tiers?: ProtoTier[];
};

type TierDiff = {
  field: string;
  from?: string;
  to?: string;
  operation: 'inserted' | 'updated' | 'deleted';
};

type ItemPricingDiff = {
  itemId: string;
  pricingUnit: string;
  tierDiffs: TierDiff[];
  operation: 'added' | 'updated' | 'deleted';
};

const extractItemPricingDiffs = (
  payload: Record<string, unknown>,
  eventType: string,
): ItemPricingDiff[] => {
  const eventData = payload[eventType] as Record<string, unknown> | undefined;
  if (!eventData) return [];

  const diffs: ItemPricingDiff[] = [];

  if (
    eventType === 'itemPricingAdded' ||
    eventType === 'copiedFrom' ||
    eventType === 'importedFrom'
  ) {
    const added = (eventData.addedItemPricings ?? []) as ProtoItemPricing[];
    for (const ip of added) {
      const tierDiffs: TierDiff[] = [];
      for (const tier of ip.tiers ?? []) {
        if (tier.targetPriceFormula) {
          tierDiffs.push({
            field: 'Target Price',
            to: `$ ${tier.targetPriceFormula}`,
            operation: 'inserted',
          });
        }
        if (tier.minPriceFormula) {
          tierDiffs.push({
            field: 'Min Price',
            to: `$ ${tier.minPriceFormula}`,
            operation: 'inserted',
          });
        }
        if (tier.maxPriceFormula) {
          tierDiffs.push({
            field: 'Max Price',
            to: `$ ${tier.maxPriceFormula}`,
            operation: 'inserted',
          });
        }
        if (tier.scrapValue && tier.scrapValue !== '0') {
          tierDiffs.push({
            field: 'Scrap Value',
            to: `$ ${tier.scrapValue}`,
            operation: 'inserted',
          });
        }
        if (tier.maxAmount) {
          tierDiffs.push({
            field: 'Max Weight',
            to: tier.maxAmount,
            operation: 'inserted',
          });
        }
      }
      diffs.push({
        itemId: ip.itemId?.value ?? '',
        pricingUnit: ip.pricingUnit ?? '',
        tierDiffs,
        operation: 'added',
      });
    }
  }

  if (
    eventType === 'itemPricingUpdated' ||
    eventType === 'itemPricingBatchUpdated'
  ) {
    const previousIPs = (eventData.previousItemPricings ??
      []) as ProtoItemPricing[];
    const updatedIPs = (eventData.updatedItemPricings ??
      []) as ProtoItemPricing[];

    for (let i = 0; i < Math.max(previousIPs.length, updatedIPs.length); i++) {
      const prev = previousIPs[i];
      const updated = updatedIPs[i];

      if (!prev && !updated) continue;

      const tierDiffs: TierDiff[] = [];
      const prevTiers = prev?.tiers ?? [];
      const updTiers = updated?.tiers ?? [];

      for (let t = 0; t < Math.max(prevTiers.length, updTiers.length); t++) {
        const pt = prevTiers[t];
        const ut = updTiers[t];

        if (pt?.targetPriceFormula !== ut?.targetPriceFormula) {
          tierDiffs.push({
            field: 'Target Price',
            from: pt?.targetPriceFormula
              ? `$ ${pt.targetPriceFormula}`
              : undefined,
            to: ut?.targetPriceFormula
              ? `$ ${ut.targetPriceFormula}`
              : undefined,
            operation: 'updated',
          });
        }
        if (pt?.minPriceFormula !== ut?.minPriceFormula) {
          tierDiffs.push({
            field: 'Min Price',
            from: pt?.minPriceFormula ? `$ ${pt.minPriceFormula}` : undefined,
            to: ut?.minPriceFormula ? `$ ${ut.minPriceFormula}` : undefined,
            operation: 'updated',
          });
        }
        if (pt?.maxPriceFormula !== ut?.maxPriceFormula) {
          tierDiffs.push({
            field: 'Max Price',
            from: pt?.maxPriceFormula ? `$ ${pt.maxPriceFormula}` : undefined,
            to: ut?.maxPriceFormula ? `$ ${ut.maxPriceFormula}` : undefined,
            operation: 'updated',
          });
        }
        if (pt?.scrapValue !== ut?.scrapValue) {
          tierDiffs.push({
            field: 'Scrap Value',
            from: pt?.scrapValue ? `$ ${pt.scrapValue}` : undefined,
            to: ut?.scrapValue ? `$ ${ut.scrapValue}` : undefined,
            operation: 'updated',
          });
        }
        if (pt?.maxAmount !== ut?.maxAmount) {
          tierDiffs.push({
            field: 'Max Weight',
            from: pt?.maxAmount ?? undefined,
            to: ut?.maxAmount ?? undefined,
            operation: 'updated',
          });
        }
      }

      if (prev?.tiersEffectiveAt !== updated?.tiersEffectiveAt) {
        tierDiffs.push({
          field: 'Effective Date',
          from: prev?.tiersEffectiveAt
            ? formatDate(prev.tiersEffectiveAt)
            : undefined,
          to: updated?.tiersEffectiveAt
            ? formatDate(updated.tiersEffectiveAt)
            : undefined,
          operation: 'updated',
        });
      }

      if (tierDiffs.length > 0) {
        diffs.push({
          itemId: (updated ?? prev)?.itemId?.value ?? '',
          pricingUnit: (updated ?? prev)?.pricingUnit ?? '',
          tierDiffs,
          operation: 'updated',
        });
      }
    }
  }

  if (eventType === 'itemPricingDeleted') {
    const deleted = (eventData.deletedItemPricings ?? []) as ProtoItemPricing[];
    for (const ip of deleted) {
      const tierDiffs: TierDiff[] = [];
      for (const tier of ip.tiers ?? []) {
        if (tier.targetPriceFormula) {
          tierDiffs.push({
            field: 'Target Price',
            from: `$ ${tier.targetPriceFormula}`,
            operation: 'deleted',
          });
        }
        if (tier.minPriceFormula) {
          tierDiffs.push({
            field: 'Min Price',
            from: `$ ${tier.minPriceFormula}`,
            operation: 'deleted',
          });
        }
        if (tier.maxPriceFormula) {
          tierDiffs.push({
            field: 'Max Price',
            from: `$ ${tier.maxPriceFormula}`,
            operation: 'deleted',
          });
        }
      }
      diffs.push({
        itemId: ip.itemId?.value ?? '',
        pricingUnit: ip.pricingUnit ?? '',
        tierDiffs,
        operation: 'deleted',
      });
    }
  }

  return diffs;
};

//
// ⚛️ ⚛️ ⚛️ PRICE LIST HISTORY DIALOG ⚛️ ⚛️ ⚛️
//
export const PriceListHistoryDialog = ({
  isOpen,
  onClose,
  priceListId,
  priceListName,
  priceListType = 'custom',
  itemNameMap,
}: PriceListHistoryDialogProps) => {
  // Translations
  const t = useTranslations();

  // Atoms
  const [, setHistoryPriceListId] = useAtom($historyPriceListId);
  const customHistory = useAtomValue($customHistoryData);
  const defaultHistory = useAtomValue($defaultHistoryData);

  const {
    data: entries,
    isFetching,
    refetch,
  } = priceListType === 'default' ? defaultHistory : customHistory;

  // Effects
  React.useEffect(() => {
    if (isOpen && priceListId) {
      setHistoryPriceListId(priceListId);
      refetch?.();
    } else if (!isOpen) {
      setHistoryPriceListId(null);
    }
  }, [isOpen, priceListId, setHistoryPriceListId, refetch]);

  return (
    <ConsoleDrawer
      className="@PriceListHistoryDialog"
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      position="right"
      title={
        <span>
          {t.pricing.customPriceLists.history.title}
          <span className="mx-1 text-neutral/30">&middot;</span>
          {priceListName}
        </span>
      }
      content={
        isFetching ? (
          <div className="space-y-4 py-2">
            <Skeleton variant="rounded" width="100%" height={48} />
            <Skeleton variant="rounded" width="80%" height={32} />
            <Skeleton variant="rounded" width="100%" height={48} />
          </div>
        ) : !entries || entries.length === 0 ? (
          <div className="py-12 text-center text-sm text-neutral/50">
            {t.pricing.customPriceLists.history.empty}
          </div>
        ) : (
          <div className="flex flex-col">
            {entries.map((entry, index) => (
              <HistoryEntryRow
                key={entry.id}
                entry={entry}
                isLast={index === entries.length - 1}
                itemNameMap={itemNameMap}
              />
            ))}
          </div>
        )
      }
    />
  );
};

type HistoryEntryRowProps = {
  entry: PriceListHistoryEntry;
  isLast: boolean;
  itemNameMap?: Map<string, string>;
};

const formatHistoryEventLabel = (
  isCreate: boolean,
  diffCount: number,
  label: string,
): string => {
  if (isCreate) return label;
  if (diffCount > 0) {
    return `${diffCount} material${diffCount !== 1 ? 's' : ''} ${label.toLowerCase()}`;
  }
  return label;
};

//
// ⚛️ ⚛️ ⚛️ HISTORY ENTRY ROW ⚛️ ⚛️ ⚛️
//
const HistoryEntryRow = ({
  entry,
  isLast,
  itemNameMap,
}: HistoryEntryRowProps) => {
  const config = EVENT_CONFIG[entry.eventType] ?? {
    label: formatEventType(entry.eventType),
    icon: <LucideCircle size={14} />,
    color: 'text-neutral/50',
  };

  const isUpdate =
    entry.eventType === 'itemPricingUpdated' ||
    entry.eventType === 'itemPricingBatchUpdated';
  const isAdd = entry.eventType === 'itemPricingAdded';
  const isDelete = entry.eventType === 'itemPricingDeleted';
  const isCreate = entry.eventType === 'created';

  // Memoized values
  const itemPricingDiffs = React.useMemo(
    () => extractItemPricingDiffs(entry.payload, entry.eventType),
    [entry.payload, entry.eventType],
  );

  const createdDetails = React.useMemo(() => {
    if (!isCreate) return null;
    const eventData = entry.payload[entry.eventType] as
      | Record<string, unknown>
      | undefined;
    if (!eventData) return null;
    const pl = (eventData.customPriceList ?? eventData) as Record<
      string,
      unknown
    >;
    return {
      name: formatValue(pl.name),
      currency: formatValue(pl.currency),
      pricingMode: formatValue(pl.pricingMode),
      operationType: formatValue(pl.operationType)
        .replace('OPERATION_TYPE_', '')
        .toLowerCase()
        .replace(/^\w/, (c) => c.toUpperCase()),
      startDate: pl.startDate ? formatDate(String(pl.startDate)) : null,
    };
  }, [entry, isCreate]);

  const diffCount = itemPricingDiffs.length;

  const eventLabel = formatHistoryEventLabel(isCreate, diffCount, config.label);

  return (
    <div className="@HistoryEntryRow grid grid-cols-[auto_1fr] gap-4">
      <div className="grid grid-rows-[auto_1fr] place-items-center">
        <LucideCircle
          size={16}
          className={cn(
            'mb-1 stroke-[3]',
            isCreate && 'text-success',
            isAdd && 'text-success',
            isUpdate && 'text-primary',
            isDelete && 'text-failure',
            !isCreate && !isAdd && !isUpdate && !isDelete && 'text-primary',
          )}
        />
        {!isLast && <div className="h-full border-l-2 border-neutral/10" />}
      </div>
      <div className="pb-6">
        <div className="text-sm text-neutral/40">
          {formatDate(entry.occurredOn)}
          <span className="mx-1">&middot;</span>
          {entry.createdByName}
        </div>
        <div className="mt-1 text-base font-bold">{eventLabel}</div>

        {isCreate && createdDetails && (
          <CreatedDetailsPanel details={createdDetails} />
        )}

        {itemPricingDiffs.length > 0 && (
          <div className="mt-3 flex flex-col">
            {itemPricingDiffs.map((ipDiff, idx) => (
              <ItemPricingDiffPanel
                key={idx}
                diff={ipDiff}
                itemName={itemNameMap?.get(ipDiff.itemId)}
              />
            ))}
          </div>
        )}

        {(isUpdate || isAdd || isDelete) && itemPricingDiffs.length === 0 && (
          <div className="mt-2 rounded bg-neutral/3 px-3 py-2 text-sm text-neutral/50">
            {isUpdate && 'Item pricing tiers were modified'}
            {isAdd && 'New item pricing was added to the list'}
            {isDelete && 'Item pricing was removed from the list'}
          </div>
        )}
      </div>
    </div>
  );
};

type CreatedDetailsPanelProps = {
  details: {
    name: string;
    operationType: string;
    pricingMode: string;
    currency: string;
    startDate: string | null;
  };
};

//
// ⚛️ ⚛️ ⚛️ CREATED DETAILS PANEL ⚛️ ⚛️ ⚛️
//
const CreatedDetailsPanel = ({ details }: CreatedDetailsPanelProps) => (
  <div className="@CreatedDetailsPanel mt-2 rounded-md border border-neutral/10 bg-neutral/3 text-sm">
    <DetailRow label="Name" value={details.name} />
    <DetailRow label="Operation Type" value={details.operationType} border />
    <DetailRow label="Pricing Mode" value={details.pricingMode} border />
    <DetailRow label="Currency" value={details.currency} border />
    {details.startDate && (
      <DetailRow label="Start Date" value={details.startDate} border />
    )}
  </div>
);

type DetailRowProps = {
  label: string;
  value: string;
  border?: boolean;
};

//
// ⚛️ ⚛️ ⚛️ DETAIL ROW ⚛️ ⚛️ ⚛️
//
const DetailRow = ({ label, value, border }: DetailRowProps) => (
  <div
    className={cn(
      '@DetailRow',
      cn(
        'grid grid-cols-[120px_1fr] gap-x-3 px-3 py-2',
        border && 'border-t border-neutral/10',
      ),
    )}
  >
    <span className="text-neutral/50">{label}</span>
    <span>{value}</span>
  </div>
);

type ItemPricingDiffPanelProps = {
  diff: ItemPricingDiff;
  itemName?: string;
};

//
// ⚛️ ⚛️ ⚛️ ITEM PRICING DIFF PANEL ⚛️ ⚛️ ⚛️
//
const ItemPricingDiffPanel = ({
  diff,
  itemName,
}: ItemPricingDiffPanelProps) => (
  <div className="@ItemPricingDiffPanel">
    {itemName && (
      <div className="mb-1 text-xs font-medium text-neutral/70">
        {itemName} ({diff.pricingUnit})
      </div>
    )}
    {diff.tierDiffs.map((td, idx) => (
      <div
        key={idx}
        className={cn(
          'grid grid-cols-2 gap-2 border-t border-neutral/10 py-1.5 text-sm',
          idx > 0 && 'border-neutral/20',
          td.operation === 'inserted' && 'bg-success/5',
          td.operation === 'deleted' && 'bg-failure/5',
        )}
      >
        <div className="flex items-center gap-1">
          {td.field.includes('Price') || td.field.includes('Scrap') ? (
            <LucideCircleDollarSign size={14} className="text-neutral/40" />
          ) : (
            <LucideWeight size={14} className="text-neutral/40" />
          )}
          <span className="text-neutral/60">
            {diff.pricingUnit && `${diff.pricingUnit}`}
            <span className="mx-0.5">&middot;</span>
            {td.field}
          </span>
        </div>
        <div className="flex items-center justify-end gap-1 tabular-nums">
          {td.from && <span className="text-neutral/40">{td.from}</span>}
          {td.from && td.to && (
            <LucideArrowRight size={14} className="mx-1 text-neutral/30" />
          )}
          {td.to && <span>{td.to}</span>}
        </div>
      </div>
    ))}
  </div>
);
